import { createContext, useContext, useState, useEffect } from 'react';
import { 
    fetchDateRangeAvailability, 
    updateDateAvailability,
    markTimeSlotBooked,
    markTimeSlotAvailable,
    fetchRecurringSchedule,
    updateRecurringSchedule as updateRecurringScheduleAPI
} from '../api/calendarAPI';
import { useAuth } from './AuthContext';
import { getFormattedMonthYear as formatMonthYear, DEFAULT_TIME_SLOTS } from '../lib/utils/dateTimeFormat';

export const CalendarContext = createContext();

export const CalendarProvider = ({ children }) => {
    const { currentUser, userRole } = useAuth();
    const [availabilityData, setAvailabilityData] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDate, setSelectedDateState] = useState(null);
    const [defaultTimeSlots, setDefaultTimeSlots] = useState(DEFAULT_TIME_SLOTS);
    const [recurringScheduleData, setRecurringScheduleData] = useState({});
    
    // Cache to reduce database calls
    const [availabilityCache, setAvailabilityCache] = useState({});
    
    // Load recurring schedule
    useEffect(() => {
        const loadRecurringSchedule = async () => {
            try {
                const response = await fetchRecurringSchedule();
                if (response.error) {
                    throw new Error(response.error.message);
                }
                setRecurringScheduleData(response.data || {});
            } catch (err) {
                setError(err.message);
            }
        };
        loadRecurringSchedule();
    }, []);
    
    // Load availability data for the current month
    useEffect(() => {
        const fetchMonthAvailability = async () => {
            setLoading(true);
            setError(null);
            
            try {
                const year = currentMonth.getFullYear();
                const month = currentMonth.getMonth();
                
                // Fix: Use local date representation instead of UTC
                const startDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;
                // Last day of the month
                const lastDay = new Date(year, month + 1, 0).getDate();
                const endDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
                
                // Check if we have this month cached
                const cacheKey = `${year}-${month}`;
                if (availabilityCache[cacheKey]) {
                    setAvailabilityData(availabilityCache[cacheKey]);
                    setLoading(false);
                    return;
                }
                
                const response = await fetchDateRangeAvailability(startDate, endDate);
                
                if (response.error) {
                    throw new Error(response.error.message);
                }
                
                setAvailabilityData(response.data || {});
                
                // Update cache
                setAvailabilityCache(prev => ({
                    ...prev,
                    [cacheKey]: response.data || {}
                }));
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        
        fetchMonthAvailability();
    }, [currentMonth, availabilityCache]);
    
    // Get days in the current month with availability data
    const getDaysInMonth = () => {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        
        // Get the first day of the month (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
        const firstDayOfMonth = new Date(year, month, 1).getDay();
        
        const days = [];
        // Add empty slots for days before the first day of the month
        for (let i = 0; i < firstDayOfMonth; i++) {
            days.push({ day: "", disabled: true });
        }
        
        // Add the days of the month
        for (let i = 1; i <= daysInMonth; i++) {
            // Create date in local timezone
            const currentDate = new Date(year, month, i);
            currentDate.setHours(0, 0, 0, 0);
            
            // Format date key in local timezone
            const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
            
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            const availabilityRecord = availabilityData[dateKey];
            
            // Check if current date is today by comparing year, month, and day
            const isToday = 
                currentDate.getFullYear() === today.getFullYear() && 
                currentDate.getMonth() === today.getMonth() && 
                currentDate.getDate() === today.getDate();
            
            // Get the day of week for this date (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
            const dayOfWeek = currentDate.getDay();
            const weekdays = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
            const weekday = weekdays[dayOfWeek];
            
            // Check if any time slots are available based on recurring schedule
            const recurringTimeSlots = recurringScheduleData[weekday] || {};
            const hasAvailableTimeSlots = defaultTimeSlots.some(time => 
                recurringTimeSlots[time] === true && 
                (!availabilityRecord?.time_slots?.[time] || 
                 (typeof availabilityRecord.time_slots[time] === 'boolean' ? 
                  availabilityRecord.time_slots[time] : 
                  availabilityRecord.time_slots[time].available !== false))
            );
            
            days.push({
                day: i,
                date: dateKey,
                disabled: currentDate < today,
                isToday: isToday,
                available: availabilityRecord ? availabilityRecord.available : hasAvailableTimeSlots,
                timeSlots: availabilityRecord ? availabilityRecord.time_slots : {},
                weekday: weekday
            });
        }
        
        return days;
    };
    
    // Get available time slots for a specific date
    const getAvailableTimeSlots = (date) => {
        if (!date) return defaultTimeSlots.map(time => ({ time, available: true }));
        
        const availabilityRecord = availabilityData[date];
        
        // Parse date in local timezone
        const [year, month, day] = date.split('-').map(Number);
        const dateObj = new Date(year, month - 1, day);
        dateObj.setHours(0, 0, 0, 0);
        
        // Get day of week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
        const dayOfWeek = dateObj.getDay();
        const weekdays = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const weekday = weekdays[dayOfWeek];
        
        // Get recurring schedule for this weekday
        const recurringTimeSlots = recurringScheduleData[weekday] || {};
        
        // If the date is explicitly marked as unavailable, return no slots
        if (availabilityRecord?.available === false) {
            return [];
        }
        
        // Start with the recurring schedule slots
        const availableSlots = defaultTimeSlots.map(time => {
            // First check if the time slot is enabled in the recurring schedule
            const isRecurringEnabled = recurringTimeSlots[time] === true;
            
            // If the slot is not enabled in recurring schedule, it's not available
            if (!isRecurringEnabled) {
                return { time, available: false };
            }
            
            // Then check if there's a specific override in the availability record
            if (availabilityRecord?.time_slots?.[time] !== undefined) {
                const slotData = availabilityRecord.time_slots[time];
                
                // Check if the slot is booked (has a booking_id)
                if (typeof slotData === 'object' && slotData.booking_id) {
                    return { time, available: false };
                }
                
                // Check if the slot is explicitly marked as unavailable
                if (typeof slotData === 'boolean' ? !slotData : slotData.available === false) {
                    return { time, available: false };
                }
            }
            
            // If we get here, the slot is:
            // 1. Enabled in the recurring schedule
            // 2. Not booked
            // 3. Not explicitly marked as unavailable
            return { time, available: true };
        });
        
        // Return only the available slots
        return availableSlots.filter(slot => slot.available);
    };
    
    // Update availability for a specific date
    const updateAvailability = async (date, isAvailable, timeSlots = {}) => {
        if (!date) return { success: false, error: { message: 'No date provided' } };
        
        // Check if user is admin
        if (userRole !== 'admin') {
            return { 
                success: false, 
                error: { message: 'Only admins can update availability' } 
            };
        }
        
        setLoading(true);
        setError(null);
        
        try {
            const response = await updateDateAvailability(date, isAvailable, timeSlots);
            
            if (response.error) {
                throw new Error(response.error.message);
            }
            
            // Update local state
            setAvailabilityData(prev => ({
                ...prev,
                [date]: response.data
            }));
            
            // Update cache
            const year = new Date(date).getFullYear();
            const month = new Date(date).getMonth();
            const cacheKey = `${year}-${month}`;
            
            setAvailabilityCache(prev => {
                if (prev[cacheKey]) {
                    return {
                        ...prev,
                        [cacheKey]: {
                            ...prev[cacheKey],
                            [date]: response.data
                        }
                    };
                }
                return prev;
            });
            
            return { success: true, data: response.data };
        } catch (err) {
            setError(err.message);
            return { success: false, error: { message: err.message } };
        } finally {
            setLoading(false);
        }
    };
    
    // Update time slot availability
    const updateTimeSlotAvailability = async (date, timeSlot, isAvailable) => {
        if (!date || !timeSlot) {
            return { success: false, error: { message: 'Date and time slot required' } };
        }
        
        // Check if user is admin
        if (userRole !== 'admin') {
            return { 
                success: false, 
                error: { message: 'Only admins can update time slot availability' } 
            };
        }
        
        setLoading(true);
        setError(null);
        
        try {
            let response;
            
            if (isAvailable) {
                // Mark time slot as available
                response = await markTimeSlotAvailable(date, timeSlot);
            } else {
                // Mark time slot as unavailable
                const availabilityRecord = availabilityData[date];
                let updatedTimeSlots = {};
                
                if (availabilityRecord && availabilityRecord.time_slots) {
                    updatedTimeSlots = { ...availabilityRecord.time_slots };
                }
                
                // Update the specific time slot
                updatedTimeSlots[timeSlot] = false;
                
                response = await updateAvailability(date, true, updatedTimeSlots);
            }
            
            if (response.error) {
                throw new Error(response.error.message);
            }
            
            // Update local state after successful update
            if (response.success && response.data) {
                setAvailabilityData(prev => ({
                    ...prev,
                    [date]: response.data
                }));
                
                // Update cache
                const year = new Date(date).getFullYear();
                const month = new Date(date).getMonth();
                const cacheKey = `${year}-${month}`;
                
                setAvailabilityCache(prev => {
                    if (prev[cacheKey]) {
                        return {
                            ...prev,
                            [cacheKey]: {
                                ...prev[cacheKey],
                                [date]: response.data
                            }
                        };
                    }
                    return prev;
                });
            }
            
            return response;
        } catch (err) {
            setError(err.message);
            return { success: false, error: { message: err.message } };
        } finally {
            setLoading(false);
        }
    };
    
    // Mark a time slot as booked when a booking is created
    const bookTimeSlot = async (date, timeSlot, bookingId = null) => {
        if (!date || !timeSlot) {
            return { success: false, error: { message: 'Date and time slot required' } };
        }
        
        setLoading(true);
        setError(null);
        
        try {
            const response = await markTimeSlotBooked(date, timeSlot, bookingId);
            
            if (response.error) {
                throw new Error(response.error.message);
            }
            
            // Update local state
            setAvailabilityData(prev => ({
                ...prev,
                [date]: response.data
            }));
            
            // Update cache
            const year = new Date(date).getFullYear();
            const month = new Date(date).getMonth();
            const cacheKey = `${year}-${month}`;
            
            setAvailabilityCache(prev => {
                if (prev[cacheKey]) {
                    return {
                        ...prev,
                        [cacheKey]: {
                            ...prev[cacheKey],
                            [date]: response.data
                        }
                    };
                }
                return prev;
            });
            
            return { success: true, data: response.data };
        } catch (err) {
            setError(err.message);
            return { success: false, error: { message: err.message } };
        } finally {
            setLoading(false);
        }
    };
    
    // Navigate to previous month
    const goToPrevMonth = () => {
        setCurrentMonth(prev => {
            const newDate = new Date(prev);
            newDate.setMonth(prev.getMonth() - 1);
            return newDate;
        });
    };
    
    // Navigate to next month
    const goToNextMonth = () => {
        setCurrentMonth(prev => {
            const newDate = new Date(prev);
            newDate.setMonth(prev.getMonth() + 1);
            return newDate;
        });
    };
    
    // Get recurring schedule
    const getRecurringSchedule = async () => {
        try {
            const response = await fetchRecurringSchedule();
            if (response.error) {
                throw new Error(response.error.message);
            }
            setRecurringScheduleData(response.data || {});
            return response.data || {};
        } catch (err) {
            setError(err.message);
            return {};
        }
    };

    // Update recurring schedule
    const updateRecurringSchedule = async (weekday, timeSlot, isAvailable) => {
        if (userRole !== 'admin') {
            return { 
                success: false, 
                error: { message: 'Only admins can update recurring schedule' } 
            };
        }

        setLoading(true);
        setError(null);

        try {
            const response = await updateRecurringScheduleAPI(weekday, timeSlot, isAvailable);
            
            if (response.error) {
                throw new Error(response.error.message);
            }

            setRecurringScheduleData(response.data);
            return { success: true, data: response.data };
        } catch (err) {
            setError(err.message);
            return { success: false, error: { message: err.message } };
        } finally {
            setLoading(false);
        }
    };
    
    // Set selected date with proper timezone handling
    const setSelectedDate = (date) => {
        if (!date) {
            setSelectedDateState(null);
            return;
        }

        // Parse the date string into components
        const [year, month, day] = date.split('-').map(Number);
        
        // Create a new date object in local timezone
        const selectedDateObj = new Date(year, month - 1, day);
        selectedDateObj.setHours(0, 0, 0, 0);
        
        // Format the date back to YYYY-MM-DD format
        const formattedDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        
        setSelectedDateState(formattedDate);
    };
    
    return (
        <CalendarContext.Provider
            value={{
                // State
                availabilityData,
                loading,
                error,
                currentMonth,
                selectedDate,
                defaultTimeSlots,
                recurringScheduleData,
                
                // Getters
                getDaysInMonth,
                getAvailableTimeSlots,
                getFormattedMonthYear: () => formatMonthYear(currentMonth),
                getRecurringSchedule,
                
                // Setters
                setSelectedDate,
                setDefaultTimeSlots,
                
                // Navigation
                goToPrevMonth,
                goToNextMonth,
                
                // Actions
                updateAvailability,
                updateTimeSlotAvailability,
                bookTimeSlot,
                updateRecurringSchedule,
                
                // Admin-only actions (can be conditionally used based on userRole)
                isAdmin: userRole === 'admin'
            }}
        >
            {children}
        </CalendarContext.Provider>
    );
};

export const useCalendar = () => {
    const context = useContext(CalendarContext);
    if (!context) {
        throw new Error('useCalendar must be used within a CalendarProvider');
    }
    return context;
};
